/**
 * DateRangePicker — Universal Reusable Date Range Filter
 * =======================================================
 * Single component for every reporting surface in the admin panel.
 *
 * Used by:
 *   - Tax Management (default consumer)
 *   - Future: Reconciliation, Financial Integrity, Orders Reports,
 *             Vendor Reports, Payout Reports, Analytics Dashboards
 *
 * Features:
 *   - 12 standard presets (Today / Yesterday / Last 7 Days / Last 30 Days /
 *     Current Month / Previous Month / Current Quarter / Previous Quarter /
 *     Current Financial Year / Previous Financial Year /
 *     Current Calendar Year / Previous Calendar Year)
 *   - Custom range picker with calendar (validates end ≥ start, blocks
 *     future dates)
 *   - URL parameter persistence (?from=YYYY-MM-DD&to=YYYY-MM-DD&preset=...)
 *   - localStorage persistence (per-key, scoped by `storageKey` prop)
 *   - Indian Financial Year aware (Apr 1 – Mar 31)
 *
 * Usage:
 *   const [range, setRange] = useState(null);
 *   <DateRangePicker
 *     value={range}
 *     onChange={setRange}
 *     storageKey="taxManagement"   // scopes localStorage
 *     defaultPreset="current_fy"   // initial selection on first mount
 *   />
 *
 *   // range = { from: Date, to: Date, preset: 'current_fy' | 'custom' | ... }
 *   // Read range.from.toISOString() / range.to.toISOString() for backend.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth,
         startOfQuarter, endOfQuarter, startOfYear, endOfYear, subMonths,
         subQuarters, subYears, isAfter, isBefore, isValid, parseISO } from 'date-fns';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { Calendar as CalendarIcon, Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// ─── Financial-year math (India: 1-Apr → 31-Mar) ─────────────────────
const fyStart = (date) => {
  const month = date.getMonth(); // 0=Jan
  const fyYear = month < 3 ? date.getFullYear() - 1 : date.getFullYear();
  return startOfDay(new Date(fyYear, 3, 1)); // Apr 1
};
const fyEnd = (date) => {
  const month = date.getMonth();
  const fyYear = month < 3 ? date.getFullYear() - 1 : date.getFullYear();
  return endOfDay(new Date(fyYear + 1, 2, 31)); // Mar 31
};
const fyLabel = (date) => {
  const start = fyStart(date);
  const end = fyEnd(date);
  return `FY ${start.getFullYear()}-${String(end.getFullYear()).slice(-2)}`;
};

// ─── 12 preset definitions ───────────────────────────────────────────
// Each preset returns { from: Date, to: Date }
export const PRESETS = [
  {
    key: 'today', label: 'Today',
    range: () => { const n = new Date(); return { from: startOfDay(n), to: endOfDay(n) }; },
  },
  {
    key: 'yesterday', label: 'Yesterday',
    range: () => { const d = subDays(new Date(), 1); return { from: startOfDay(d), to: endOfDay(d) }; },
  },
  {
    key: 'last_7_days', label: 'Last 7 Days',
    range: () => { const n = new Date(); return { from: startOfDay(subDays(n, 6)), to: endOfDay(n) }; },
  },
  {
    key: 'last_30_days', label: 'Last 30 Days',
    range: () => { const n = new Date(); return { from: startOfDay(subDays(n, 29)), to: endOfDay(n) }; },
  },
  {
    key: 'current_month', label: 'Current Month',
    range: () => { const n = new Date(); return { from: startOfMonth(n), to: endOfDay(n) }; },
  },
  {
    key: 'previous_month', label: 'Previous Month',
    range: () => { const m = subMonths(new Date(), 1); return { from: startOfMonth(m), to: endOfMonth(m) }; },
  },
  {
    key: 'current_quarter', label: 'Current Quarter',
    range: () => { const n = new Date(); return { from: startOfQuarter(n), to: endOfDay(n) }; },
  },
  {
    key: 'previous_quarter', label: 'Previous Quarter',
    range: () => { const q = subQuarters(new Date(), 1); return { from: startOfQuarter(q), to: endOfQuarter(q) }; },
  },
  {
    key: 'current_fy', label: 'Current Financial Year',
    range: () => { const n = new Date(); return { from: fyStart(n), to: endOfDay(n) }; },
    secondaryLabel: () => fyLabel(new Date()),
  },
  {
    key: 'previous_fy', label: 'Previous Financial Year',
    range: () => {
      const lastFy = subYears(new Date(), 1);
      return { from: fyStart(lastFy), to: fyEnd(lastFy) };
    },
    secondaryLabel: () => fyLabel(subYears(new Date(), 1)),
  },
  {
    key: 'current_cy', label: 'Current Calendar Year',
    range: () => { const n = new Date(); return { from: startOfYear(n), to: endOfDay(n) }; },
  },
  {
    key: 'previous_cy', label: 'Previous Calendar Year',
    range: () => { const y = subYears(new Date(), 1); return { from: startOfYear(y), to: endOfYear(y) }; },
  },
];

const PRESET_BY_KEY = PRESETS.reduce((m, p) => ({ ...m, [p.key]: p }), {});
const ISO_DATE = (d) => format(d, 'yyyy-MM-dd');

// ─── Persistence helpers ─────────────────────────────────────────────
const lsKey = (storageKey) => `aniyor.daterange.${storageKey || 'default'}`;

const readLocalStorage = (storageKey) => {
  try {
    const raw = localStorage.getItem(lsKey(storageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.from || !parsed.to) return null;
    const from = parseISO(parsed.from);
    const to = parseISO(parsed.to);
    if (!isValid(from) || !isValid(to)) return null;
    return { from, to, preset: parsed.preset || 'custom' };
  } catch { return null; }
};

const writeLocalStorage = (storageKey, range) => {
  try {
    localStorage.setItem(lsKey(storageKey), JSON.stringify({
      from: ISO_DATE(range.from), to: ISO_DATE(range.to), preset: range.preset,
    }));
  } catch { /* ignore (quota / private mode) */ }
};

// ─── Display label ───────────────────────────────────────────────────
const displayLabel = (range) => {
  if (!range || !range.from || !range.to) return 'Select date range';
  if (range.preset && range.preset !== 'custom') {
    const p = PRESET_BY_KEY[range.preset];
    if (p) return p.secondaryLabel ? `${p.label} · ${p.secondaryLabel()}` : p.label;
  }
  return `${format(range.from, 'd MMM yyyy')} – ${format(range.to, 'd MMM yyyy')}`;
};

// ─── Main component ──────────────────────────────────────────────────
export default function DateRangePicker({
  value,                    // controlled { from, to, preset } | null
  onChange,                 // (range) => void
  defaultPreset = 'current_fy',
  storageKey,               // e.g. 'taxManagement' — namespaces localStorage + URL
  syncUrl = true,           // whether to read/write URL params
  className,
  align = 'end',
  testid = 'date-range-picker',
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('presets'); // 'presets' | 'custom'
  const [customRange, setCustomRange] = useState(value ? { from: value.from, to: value.to } : undefined);
  const initialisedRef = useRef(false);

  // ── Initial hydration: URL > localStorage > defaultPreset ──────────
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    if (value) return; // caller already controls value

    // 1) URL params
    if (syncUrl) {
      const urlPreset = searchParams.get(`${storageKey || 'dr'}_preset`);
      const urlFrom = searchParams.get(`${storageKey || 'dr'}_from`);
      const urlTo = searchParams.get(`${storageKey || 'dr'}_to`);
      if (urlPreset && PRESET_BY_KEY[urlPreset]) {
        const r = PRESET_BY_KEY[urlPreset].range();
        onChange({ ...r, preset: urlPreset });
        return;
      }
      if (urlFrom && urlTo) {
        const from = parseISO(urlFrom);
        const to = parseISO(urlTo);
        if (isValid(from) && isValid(to)) {
          onChange({ from, to, preset: 'custom' });
          return;
        }
      }
    }
    // 2) localStorage
    const ls = readLocalStorage(storageKey);
    if (ls) { onChange(ls); return; }
    // 3) Default preset
    if (PRESET_BY_KEY[defaultPreset]) {
      const r = PRESET_BY_KEY[defaultPreset].range();
      onChange({ ...r, preset: defaultPreset });
    }
  }, [value, onChange, defaultPreset, storageKey, syncUrl, searchParams]);

  // ── Persist on every change ────────────────────────────────────────
  useEffect(() => {
    if (!value || !value.from || !value.to) return;
    writeLocalStorage(storageKey, value);
    if (syncUrl) {
      // We mutate the SAME searchParams instance so other pickers /
      // filters aren't blown away.
      const next = new URLSearchParams(searchParams);
      const ns = storageKey || 'dr';
      if (value.preset && value.preset !== 'custom') {
        next.set(`${ns}_preset`, value.preset);
        next.delete(`${ns}_from`);
        next.delete(`${ns}_to`);
      } else {
        next.set(`${ns}_from`, ISO_DATE(value.from));
        next.set(`${ns}_to`, ISO_DATE(value.to));
        next.delete(`${ns}_preset`);
      }
      setSearchParams(next, { replace: true });
    }
  }, [value, storageKey, syncUrl, searchParams, setSearchParams]);

  // ── Handlers ──────────────────────────────────────────────────────
  const selectPreset = useCallback((key) => {
    const p = PRESET_BY_KEY[key];
    if (!p) return;
    const r = p.range();
    onChange({ ...r, preset: key });
    setOpen(false);
  }, [onChange]);

  const applyCustom = useCallback(() => {
    if (!customRange?.from || !customRange?.to) return;
    if (isAfter(customRange.from, customRange.to)) return;
    onChange({
      from: startOfDay(customRange.from),
      to: endOfDay(customRange.to),
      preset: 'custom',
    });
    setOpen(false);
  }, [customRange, onChange]);

  const today = useMemo(() => endOfDay(new Date()), []);
  const customValid = customRange?.from && customRange?.to
    && !isAfter(customRange.from, customRange.to)
    && !isAfter(customRange.to, today)
    && !isAfter(customRange.from, today);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-9 gap-2 min-w-[200px] justify-between', className)}
          data-testid={testid}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarIcon className="h-4 w-4 text-slate-500" />
            <span className="truncate">{displayLabel(value)}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-slate-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto p-0 flex"
        data-testid={`${testid}-popover`}
      >
        {/* Presets column */}
        <div className="w-56 border-r border-slate-200 p-2 space-y-0.5 max-h-[28rem] overflow-y-auto">
          {PRESETS.map((p) => {
            const active = value?.preset === p.key && mode === 'presets';
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => selectPreset(p.key)}
                className={cn(
                  'w-full text-left px-3 py-1.5 rounded-md text-sm flex items-center justify-between gap-2',
                  active
                    ? 'bg-slate-900 text-white'
                    : 'hover:bg-slate-100 text-slate-700',
                )}
                data-testid={`${testid}-preset-${p.key}`}
              >
                <span className="truncate">
                  {p.label}
                  {p.secondaryLabel && (
                    <span className={cn('block text-xs', active ? 'text-slate-300' : 'text-slate-400')}>
                      {p.secondaryLabel()}
                    </span>
                  )}
                </span>
                {active && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
              </button>
            );
          })}
          <div className="my-1 border-t border-slate-200" />
          <button
            type="button"
            onClick={() => setMode('custom')}
            className={cn(
              'w-full text-left px-3 py-1.5 rounded-md text-sm',
              mode === 'custom' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-700',
            )}
            data-testid={`${testid}-preset-custom`}
          >
            Custom Range…
          </button>
        </div>

        {/* Custom calendar column — only when 'custom' mode active */}
        {mode === 'custom' && (
          <div className="p-3 space-y-3" data-testid={`${testid}-custom-pane`}>
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={customRange?.from || subMonths(new Date(), 1)}
              selected={customRange}
              onSelect={setCustomRange}
              disabled={(d) => isAfter(d, today)}
              data-testid={`${testid}-calendar`}
            />
            <div className="flex items-center justify-between text-xs text-slate-500 px-1">
              <span>
                {customRange?.from && customRange?.to
                  ? `${format(customRange.from, 'd MMM yyyy')} – ${format(customRange.to, 'd MMM yyyy')}`
                  : 'Pick a start and end date'}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setMode('presets'); setCustomRange(undefined); }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={applyCustom}
                  disabled={!customValid}
                  data-testid={`${testid}-apply-custom`}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Re-exports for consumers ───────────────────────────────────────
export { fyLabel, fyStart, fyEnd, ISO_DATE };
